import React, { useState } from "react";
import { FiArrowUpRight, FiBarChart2, FiInfo } from "react-icons/fi";
import { Link } from "react-router-dom";
import CoinIcon from "../components/CoinIcon";
import { useBinanceTicker } from "../services/BinanceTickerContext";
import { marketAsset, marketChange, marketName, marketNetworks, marketNumber, marketPrice, marketSymbol, useMarketAssets } from "../services/useMarketAssets";

function UnlockRow({ market }) {
  const asset = marketAsset(market);
  const symbol = marketSymbol(market);
  const ticker = useBinanceTicker(symbol);
  const totalSupply = marketNumber(market.total_supply);
  const totalUnlocked = marketNumber(market.total_unlocked);
  const unlockAmount = marketNumber(market.next_unlock_amount);
  const unlockedPercent = totalSupply && totalUnlocked != null ? Math.min(totalUnlocked / totalSupply * 100, 100) : null;
  const networks = marketNetworks(market).map((network) => network.name ?? network.symbol).filter(Boolean).join(", ");

  return (
    <div className="unlock-row" role="row">
      <span className="unlock-asset" role="cell">
        <CoinIcon symbol={symbol} logo={asset.logo} name={marketName(market)} />
        <span><b>{symbol}</b><small>{marketName(market)}{networks ? ` · ${networks}` : ""}</small></span>
      </span>
      <span role="cell">{marketPrice(ticker)}<small className={marketChange(ticker).startsWith("+") ? "up" : "down"}>{marketChange(ticker)}</small></span>
      <span role="cell">{totalSupply == null ? "—" : totalSupply.toLocaleString("en-US")}</span>
      <span role="cell">{totalUnlocked == null ? "—" : totalUnlocked.toLocaleString("en-US")}</span>
      <span role="cell">
        {unlockedPercent == null ? "—" : <><progress max="100" value={unlockedPercent} /><small>{unlockedPercent.toFixed(2)}%</small></>}
      </span>
      <span role="cell">{unlockAmount == null ? "—" : unlockAmount.toLocaleString("en-US")}</span>
      <span role="cell">{market.next_unlock_at ? new Date(market.next_unlock_at).toLocaleDateString() : "—"}</span>
      <span className="trading-market-actions" role="cell">
        <Link to={`/trade?symbol=${encodeURIComponent(market.market_symbol ?? market.symbol ?? `${symbol}USDT`)}`} aria-label={`Trade ${symbol}`} title={`Trade ${symbol}`}><FiBarChart2 /></Link>
        <Link to={`/markets/asset/${encodeURIComponent(symbol)}`} aria-label={`View ${symbol} details`} title={`View ${symbol} details`}><FiInfo /></Link>
      </span>
    </div>
  );
}

const TokenUnlock = () => {
  const { assets, loading, error } = useMarketAssets({ token_unlocks: true });
  const [category, setCategory] = useState("all");
  const categories = [...new Set(assets.map((market) => market.category).filter(Boolean))];
  const filteredAssets = category === "all" ? assets : assets.filter((market) => market.category === category);

  return (
    <section className="market-data-page">
      <div className="market-data-heading">
        <div><h1>Token Unlock</h1><p>Unlock schedules managed by administrators for active markets.</p></div>
        <Link to="/markets/overview">Markets overview <FiArrowUpRight aria-hidden="true" /></Link>
      </div>
      {categories.length > 0 && <div className="market-data-filters"><select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Filter by category"><option value="all">All categories</option>{categories.map((value) => <option key={value} value={value}>{value}</option>)}</select></div>}
      {error && <p className="market-state market-state-error" role="alert">{error}</p>}
      <div className="unlock-table" role="table" aria-label="Token unlock schedule">
        <div className="unlock-head" role="row"><span role="columnheader">Asset / Network</span><span role="columnheader">Price / 24h</span><span role="columnheader">Total Supply</span><span role="columnheader">Unlocked</span><span role="columnheader">Progress</span><span role="columnheader">Next Unlock</span><span role="columnheader">Unlock Date</span><span role="columnheader">Actions</span></div>
        {filteredAssets.map((market) => <UnlockRow key={marketSymbol(market)} market={market} />)}
        {!loading && !error && filteredAssets.length === 0 && <p className="market-state">No token unlock schedules are configured.</p>}
        {loading && <p className="market-state">Loading token unlocks…</p>}
      </div>
    </section>
  );
};

export default TokenUnlock;
